import React, { useId } from "react";

const baseInput = {
  width: "100%",
  borderRadius: 12,
  border: "1px solid rgba(15, 23, 42, 0.12)",
  padding: "10px 12px",
  minHeight: 44,
  background: "rgba(255,255,255,0.85)",
  fontSize: 14,
  outline: "none",
};

export function Input({ label, value, onChange, placeholder, type = "text", disabled = false }) {
  const inputId = useId();
  return (
    <div style={{ marginBottom: 12 }}>
      {label && <label htmlFor={inputId} style={{ display: "block", fontWeight: 600, marginBottom: 6 }}>{label}</label>}
      <input
        id={inputId}
        value={value}
        type={type}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        style={{
          ...baseInput,
          opacity: disabled ? 0.72 : 1,
          cursor: disabled ? "not-allowed" : "text",
        }}
      />
    </div>
  );
}

export function Select({ label, value, onChange, options, disabled = false }) {
  const selectId = useId();
  return (
    <div style={{ marginBottom: 12 }}>
      {label && <label htmlFor={selectId} style={{ display: "block", fontWeight: 600, marginBottom: 6 }}>{label}</label>}
      <select
        id={selectId}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        style={{
          ...baseInput,
          opacity: disabled ? 0.72 : 1,
          cursor: disabled ? "not-allowed" : "pointer",
        }}
      >
        <option value="">Select…</option>
        {options.map((opt) => (
          <option key={opt.value || opt} value={opt.value || opt}>
            {opt.label || opt}
          </option>
        ))}
      </select>
    </div>
  );
}

export function TextArea({ label, value, onChange, rows = 4, placeholder, disabled = false }) {
  const textAreaId = useId();
  return (
    <div style={{ marginBottom: 12 }}>
      {label && <label htmlFor={textAreaId} style={{ display: "block", fontWeight: 600, marginBottom: 6 }}>{label}</label>}
      <textarea
        id={textAreaId}
        value={value}
        rows={rows}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        style={{
          ...baseInput,
          minHeight: 120,
          resize: "vertical",
          opacity: disabled ? 0.72 : 1,
          cursor: disabled ? "not-allowed" : "text",
        }}
      />
    </div>
  );
}

export function Button({ children, onClick, tone = "primary", icon, full, disabled, loading = false, loadingLabel }) {
  const palette = {
    primary: { bg: "linear-gradient(120deg, #19a463, #0c3b2e)", color: "#fff" },
    secondary: { bg: "#0f172a", color: "#fff" },
    ghost: { bg: "rgba(25,164,99,0.08)", color: "#0c3b2e" },
  }[tone];
  const isDisabled = disabled || loading;

  return (
    <button
      onClick={isDisabled ? undefined : onClick}
      disabled={isDisabled}
      aria-busy={loading ? "true" : undefined}
      style={{
        border: "none",
        borderRadius: 12,
        padding: "10px 16px",
        minHeight: 44,
        background: isDisabled ? "rgba(15,23,42,0.12)" : palette.bg,
        color: palette.color,
        cursor: loading ? "wait" : disabled ? "not-allowed" : "pointer",
        fontWeight: 700,
        width: full ? "100%" : "auto",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        boxShadow: isDisabled ? "none" : "0 10px 30px rgba(12,59,46,0.18)",
      }}
    >
      {loading ? <span className="button-spinner" aria-hidden="true" /> : icon}
      {loading && loadingLabel ? loadingLabel : children}
    </button>
  );
}

export function Card({ title, action, children, tone = "default" }) {
  const border =
    tone === "accent"
      ? "1px solid rgba(25,164,99,0.2)"
      : "1px solid rgba(15,23,42,0.06)";
  return (
    <div
      style={{
        background: "var(--card)",
        borderRadius: "var(--radius)",
        padding: 18,
        border,
        boxShadow: "var(--shadow)",
        overflowX: "auto",
      }}
    >
      {(title || action) && (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 10,
            marginBottom: 10,
          }}
        >
          {title && <div style={{ fontWeight: 700, fontSize: 16 }}>{title}</div>}
          {action}
        </div>
      )}
      {children}
    </div>
  );
}

export function Pill({ children }) {
  return (
    <span
      style={{
        background: "rgba(25,164,99,0.12)",
        color: "#0c3b2e",
        borderRadius: 999,
        padding: "4px 10px",
        fontWeight: 700,
        fontSize: 12,
      }}
    >
      {children}
    </span>
  );
}

export function JsonPreview({ data, label = "Response" }) {
  return (
    <Card title={label} tone="accent">
      <pre
        style={{
          margin: 0,
          background: "#0f172a",
          color: "#e2fbe8",
          padding: 14,
          borderRadius: 12,
          maxHeight: 320,
          overflow: "auto",
          fontSize: 12,
        }}
      >
        {data ? JSON.stringify(data, null, 2) : "No data yet"}
      </pre>
    </Card>
  );
}

export function TwoColumn({ left, right }) {
  return (
    <div className="two-column">
      <div>{left}</div>
      <div>{right}</div>
    </div>
  );
}

export function Toast({ toast, onClose }) {
  if (!toast) return null;
  const palette = toast.tone === "error"
    ? { bg: "#fee2e2", color: "#991b1b", border: "1px solid #fecaca" }
    : { bg: "#e2fbe8", color: "#065f46", border: "1px solid #bbf7d0" };
  return (
    <div
      style={{
        position: "fixed",
        top: 16,
        right: 16,
        padding: "12px 14px",
        borderRadius: 12,
        background: palette.bg,
        color: palette.color,
        border: palette.border,
        boxShadow: "0 12px 30px rgba(0,0,0,0.1)",
        zIndex: 3000,
        minWidth: 240,
        maxWidth: "min(420px, calc(100vw - 32px))",
        fontWeight: 700,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 10,
      }}
    >
      <span>{toast.message}</span>
      <button
        onClick={onClose}
        style={{
          border: "none",
          background: "transparent",
          color: palette.color,
          cursor: "pointer",
          fontWeight: 900,
          fontSize: 14,
        }}
        aria-label="Close"
      >
        ✕
      </button>
    </div>
  );
}
